import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, HostBinding, Inject, Input, OnChanges, OnInit, Optional } from '@angular/core';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { CustomActiveStateDirective } from '@frontend/sports/common/ui/custom-active-state';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { NOT_APPLICABLE, TRACKING_NO_GAMEDETAILS, trackingConstants } from '@frontend/sports/tracking/feature';
import { Store } from '@ngrx/store';
import { IBetslipRootState, IBetslipState, betslipFeatureKey } from 'packages/sports/common/betslip/base/store/state';
import { ExternalBetslipActions } from 'packages/sports/common/betslip/core/external-betslip-actions';
import { V2OptionMarketPickId } from 'packages/sports/common/betslip/core/picks/pick-id';
import { IPickTracking, ParticipantPickType } from 'packages/sports/common/betslip/core/picks/pick-models';
import { BetslipV2HorseRaceOptionMarketPick } from 'packages/sports/common/betslip/core/picks/sport-specific/betslip-v2-horse-race-picks';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';
import {
    BETSLIP_LINE_SWITCHER_INJECTION_TOKEN,
    ILineSwitcherPickHandlerService,
} from 'packages/sports/common/betslip/integration/services/line-switcher-pick-handler.service';
import { betslipPickStateSelector } from 'packages/sports/common/betslip/modules/picks/selectors';
import { map, take } from 'rxjs';

import { GeolocationCheckService } from '../geo-location/geo-location-check.service';
import StoragePersister from '../store-persist/storage-persister';
import { OptionIndicatorDirective } from './option-indicator.directive';
import { PickSourceProvider } from './pick-source.provider';
import { PickComponent } from './pick.component';
import { WinPickDelayService } from './winpickdelayservice.service';

@Component({
    selector: 'ms-hr-option-pick',
    templateUrl: 'pick.component.html',
    imports: [CustomActiveStateDirective, NgClass, OptionIndicatorDirective, AutomationModule, AsyncPipe],
})
export class HrOptionPickComponent extends PickComponent implements OnChanges, OnInit {
    @HostBinding('class') className = 'hr-option-pick';
    @Input() participant: WinGameParticipant;
    @Input() option: EventOption;
    @Input() optionGroup: EventOptionGroup;
    @Input() event: WinGameFixture;
    @Input() pickType = ParticipantPickType.Derived;
    @Input() customOptionId: number;
    @Input() trackingOptions?: { [key: string]: string };
    @Input() checkSiblingSelected = false;

    override pickId: V2OptionMarketPickId;
    private pickSource: IPickTracking;
    siblingPickId: V2OptionMarketPickId;
    siblingOption: EventOption | undefined;

    constructor(
        geolocationCheckService: GeolocationCheckService,
        betslipIntegration: BetslipIntegrationService,
        private pickSourceProvider: PickSourceProvider,
        private store: Store<IBetslipRootState>,
        private storagePersister: StoragePersister,
        private winPickDelayService: WinPickDelayService,
        changeDetection: ChangeDetectorRef,
        @Inject(BETSLIP_LINE_SWITCHER_INJECTION_TOKEN) @Optional() lineSwitcherPickHandlerService: ILineSwitcherPickHandlerService | null,
    ) {
        super(geolocationCheckService, betslipIntegration, changeDetection, lineSwitcherPickHandlerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.pickTracking();
    }

    get price(): NativePrice {
        return this.option?.nativePrice;
    }

    get priceId(): number | undefined {
        return this.option?.id;
    }

    private createPickId(): V2OptionMarketPickId {
        return new V2OptionMarketPickId(this.event.id, this.option.optionGroupId, this.option.id, this.option.priceId!);
    }

    protected addPick(): void {
        this.pickSource.source = 'RaceCardPage';
        this.pickTracking();
        if (!this.winPickDelayService.isPickExist(this.siblingPickId?.toString())) {
            this.winPickDelayService.addPick(this.pickId.toString());
            if (this.isTogglePick()) {
                this.store.dispatch(ExternalBetslipActions.toggleWinPick({ pickId: this.siblingPickId }));
            } else {
                this.store.dispatch(ExternalBetslipActions.addPick({ pickId: this.pickId, tracking: this.pickSource, priceId: this.option.priceId }));
            }
        }
    }

    protected removePick(): void {
        if (this.pickType === ParticipantPickType.Win && this.siblingPickId && this.checkSiblingSelected) {
            const pickState$ = this.store.select(betslipPickStateSelector);

            const siblingPick$ = pickState$.pipe(map((state) => state.pickList.find((t) => t.id.toString() === this.siblingPickId.toString())));

            siblingPick$.pipe(take(1)).subscribe((siblingPick) => {
                if (siblingPick && siblingPick instanceof BetslipV2HorseRaceOptionMarketPick) {
                    this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.siblingPickId }));
                } else {
                    this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.pickId }));
                }
            });
        } else {
            this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.pickId }));
        }
    }

    ngOnChanges(changes: ISimpleChanges<HrOptionPickComponent>): void {
        this.state.locked = !this.option.online || !this.optionGroup.online;
        this.setSiblingOption();
        if (this.checkSiblingSelected && this.pickType === ParticipantPickType.Win) {
            this.setPickId(this.createPickId(), undefined, undefined, this.siblingPickId);
        } else {
            this.setPickId(this.createPickId());
        }

        this.pickTracking();
    }

    private setSiblingOption(): void {
        if (this.pickType === ParticipantPickType.Win && !this.state.locked) {
            this.siblingOption = this.event.optionGroups
                .find((og) => og.id === this.option.optionGroupId.toString())
                ?.options.find((o) => o.id !== this.option.id && o.optionPlayerId === this.option.optionPlayerId);
            if (this.siblingOption) {
                this.siblingPickId = this.createAltPickId(
                    this.event.id,
                    this.option.optionGroupId,
                    this.siblingOption.id,
                    this.siblingOption.priceId!,
                );
            }
        }
    }

    private createAltPickId(fixtureId: string, marketId: number, optionId: number, priceId: number): V2OptionMarketPickId {
        const option: { [id: number]: number } = {};
        const price: { [id: number]: number } = {};
        option[optionId] = 1;
        price[priceId] = 1;

        return new V2OptionMarketPickId(fixtureId, marketId, optionId, priceId);
    }

    private pickTracking() {
        const pickSource = this.pickSourceProvider.get(
            this.trackingSource,
            this.trackingPosition,
            this.isFallbackMarketEnabled,
            this.marqueeName,
            this.sitecoreTemplateId,
            this.marqueeType,
            undefined,
            this.isAutomatedMarquee,
        );

        pickSource.additional = { ...(pickSource.additional ?? {}), ...(this.trackingOptions ?? {}) };
        const betDetails = this.trackingOptions ? this.trackingOptions[trackingConstants.BET_DETAILS] : undefined;
        pickSource.gameDetails = this.trackingGameDetails ?? TRACKING_NO_GAMEDETAILS;
        pickSource.sourceOfPick = this.sourceOfPick ?? NOT_APPLICABLE;
        pickSource.betDetails = this.betDetails ?? betDetails ?? NOT_APPLICABLE;
        pickSource.betType = this.optionGroup.name;
        pickSource.contentPosition = this.trackingPosition?.toString();
        pickSource.moduleType = this.trackingModuleType ?? NOT_APPLICABLE;
        pickSource.source = this.trackingSource;
        if (this.event.fixtureType === 'Antepost') {
            pickSource.sheetviewSuffix = '/future';
        }
        this.pickSource = pickSource;
    }

    private isTogglePick(): boolean {
        if (this.pickType === ParticipantPickType.Win && this.siblingPickId) {
            const savedState = this.storagePersister.load();
            if (savedState && betslipFeatureKey in savedState) {
                const betslipState = savedState[betslipFeatureKey] as IBetslipState;
                if (betslipState?.picks?.pickList?.find((t) => t.id.toString() === this.siblingPickId.toString())) {
                    return true;
                }
            }
        }

        return false;
    }
}
